/**
 * CreateExperimentsModal
 *
 *
 * Design source: Figma (Step 1),(Step 2)
 *
 * Features:
 *   - Scrim overlay with glass effect dialog
 *   - Step indicator (Step 1 / Step 2) with checkmark on completed step
 *   - Step 1: Define experiment (title, description, file upload)
 *   - Step 2: Processing options (SHM, Clonotyping)
 *   - Footer with Back/Cancel/Next/Create buttons
 *   - Uses Mantine useForm for form state management (same pattern as original modal)
 */

import React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { v4 as uuidv4 } from 'uuid';

import { CreateExperimentStep1 } from './CreateExperimentStep1';
import { CreateExperimentStep2 } from './CreateExperimentStep2';
import type { CreateExperimentFormValues } from './interfaces';
import backButtonIcon from '../assets/back-button.svg';
import crossMarkIcon from '../assets/crossmark.svg';
import stepCheckmarkIcon from '../assets/step-checkmark.svg';
import { TITLE_MAX_LENGTH, validateDescription, validateTitle } from '../constants/constants';
import type { ClonotypingMethod, ExperimentCreate } from '../generated/generatedOpenAPI';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { ClonotypeDefinition, setClonotypeDefinition } from '../store/slices/preprocessing.slice';
import { useCreateExperiment, useDeleteExperiment, useLazyInitializeExperiment } from '../store/store';
import { Organism } from '../types/experiment';
import { getClonotypeEnumFromDefinition, getErrorMessage } from '../utils';

/* ─── Form → API payload mapper ───────────────────────────────────── */

function buildExperimentCreatePayload(values: CreateExperimentFormValues): ExperimentCreate {
  return {
    id: values.id,
    title: values.title,
    description: values.description,
    organism: values.organism as 'mouse' | 'human',
    cellFile: undefined,
    antibodyFile: values.candidateInformation,
    abFormat: '10X',
    clonotypeDefinition: [getClonotypeEnumFromDefinition(values.clonotypeDefinition) as ClonotypingMethod],
    threshold: values.threshold,
    includeUnpairedHeavyChains: values.includeUnpairedHeavyChains,
  };
}

/* ─── Form initial values ──────────────────────────────────────────── */

const createInitialFormValues = (): CreateExperimentFormValues => ({
  id: uuidv4(),
  title: '',
  description: '',
  organism: Organism.MOUSE,
  candidateInformation: '',
  useDefaultClonotyping: true,
  clonotypeDefinition: ClonotypeDefinition.HILARY,
  threshold: 0.85,
  includeUnpairedHeavyChains: true,
});

/* ─── Component ────────────────────────────────────────────────────── */

export interface CreateExperimentsModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback to close the modal */
  close: () => void;
}

export function CreateExperimentsModal({ opened, close }: CreateExperimentsModalProps) {
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = React.useState(1);
  const [hasInitialized, setHasInitialized] = React.useState(false);
  const [shouldCleanupOnClose, setShouldCleanupOnClose] = React.useState(true);

  const initialFormValues = React.useMemo(() => createInitialFormValues(), []);

  const form = useForm<CreateExperimentFormValues>({
    initialValues: initialFormValues,
    validate: {
      title: validateTitle,
      description: validateDescription,
      candidateInformation: (value) => (value ? undefined : 'Antibody information is required'),
      threshold: (value) => (value >= 0 ? undefined : 'Threshold is required'),
    },
  });

  /* - Experiment Initialization (lazy — triggered on first file drop) ─── */

  const [initializeExperiment] = useLazyInitializeExperiment();

  const handleFileDrop = React.useCallback(async () => {
    if (hasInitialized) {
      return;
    }

    try {
      await initializeExperiment({
        experimentInitialize: {
          id: form.values.id,
          organism: form.values.organism as 'mouse' | 'human',
          antibodyFile: form.values.candidateInformation,
          cellFile: null,
          abFormat: '10X' as const,
        },
      }).unwrap();
      setHasInitialized(true);
    } catch (error) {
      showNotification({
        title: 'Failed to initialize experiment',
        message: getErrorMessage(error, 'Experiment could not be initialized'),
        color: 'red',
        autoClose: 4000,
      });
      throw error; // re-throw so FileUpload's onFileDrop handler aborts the upload
    }
  }, [hasInitialized, initializeExperiment, form.values.id, form.values.organism, form.values.candidateInformation]);

  /* ─── Create & Delete hooks ── */

  const [createExperiment, { isLoading: isCreatingExperiment }] = useCreateExperiment();
  const [deleteExperiment] = useDeleteExperiment();

  /* ─── Reset on close ──── */

  React.useEffect(() => {
    if (!opened) {
      setActiveStep(1);
      setHasInitialized(false);
      setShouldCleanupOnClose(true);
    }
  }, [opened]);

  /* ─── Abort / Cancel ── */

  const handleAbortExperimentCreation = React.useCallback(async () => {
    const resetFormState = () => {
      form.reset();
      setActiveStep(1);
      setHasInitialized(false);
    };

    if (!shouldCleanupOnClose) {
      setShouldCleanupOnClose(true);
      resetFormState();
      return;
    }

    if (!hasInitialized) {
      resetFormState();
      return;
    }

    try {
      await deleteExperiment({ id: form.values.id }).unwrap();
    } catch (error) {
      showNotification({
        title: 'Failed to delete experiment',
        message: getErrorMessage(error, 'Experiment could not be deleted'),
        color: 'red',
        autoClose: 2000,
      });
    } finally {
      setShouldCleanupOnClose(true);
      resetFormState();
    }
  }, [deleteExperiment, form, hasInitialized, shouldCleanupOnClose]);

  const handleClose = React.useCallback(async () => {
    await handleAbortExperimentCreation();
    close();
  }, [handleAbortExperimentCreation, close]);

  /* ─── Create experiment ── */

  const handleCreate = React.useCallback(async () => {
    const { values } = form;

    try {
      dispatch(setClonotypeDefinition({ experimentId: values.id, clonotypeDefinition: values.clonotypeDefinition }));

      await createExperiment({
        experimentCreate: buildExperimentCreatePayload(values),
      }).unwrap();

      setShouldCleanupOnClose(false);
      setHasInitialized(false);
      form.reset();
      setActiveStep(1);
      close();
      showNotification({
        title: 'Experiment created',
        message: 'Experiment has been created successfully',
        color: 'green',
        autoClose: 2000,
      });
    } catch (error) {
      showNotification({
        title: 'Failed to create experiment',
        message: getErrorMessage(error, 'Experiment could not be created'),
        color: 'red',
        autoClose: 2000,
      });
    }
  }, [createExperiment, close, dispatch, form]);

  /* ─── Step Navigation ─────────────────────────────────────────── */

  const handleNext = () => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    if (activeStep === 2) {
      setActiveStep(1);
    }
  };

  const uploadState = useAppSelector((state) => state.uploads?.[form.values.id]);

  const hasStep1Errors =
    form.values.title?.length === 0 ||
    form.values.title?.length > TITLE_MAX_LENGTH ||
    form.values.description?.length > 255 ||
    !hasInitialized ||
    !form.values.candidateInformation ||
    uploadState?.phase === 'failed';

  if (!opened) {
    return null;
  }

  return createPortal(
    <div className="modal-scrim">
      {/* Dialog — LAYER 1: blur effect */}
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        {/* LAYER 2: color tint over the blur */}
        <div className="modal-content-tint">
          {/* Content */}
          <div className="flex flex-col items-center w-full h-full min-h-0">
            {/* Header */}
            <div className="flex flex-col self-stretch gap-3 pt-6">
              {/* Title Row */}
              <div className="flex items-center self-stretch px-6 relative">
                {/* Title - Centered */}
                <h2 id="modal-title" className="flex-1 text-center font-['Rubik'] font-medium text-base text-neutral-800">
                  New Experiment
                </h2>

                {/* Close button - Absolute right */}
                <Button variant="subtle" onClick={handleClose} className="absolute right-6 top-0" p={0} w={32} h={32} aria-label="Close modal">
                  <img src={crossMarkIcon} alt="Close" className="w-4 h-4" />
                </Button>
              </div>

              {/* Stepper */}
              <div className="flex flex-col justify-center items-center gap-2 px-[90px] pb-7 w-full">
                <div className="flex items-center gap-2">
                  {/* Step 1 circle - shows checkmark when completed */}
                  <div className="w-[34px] h-[34px] rounded-full flex items-center justify-center bg-transparent border-solid border-2 border-brand-blue">
                    {activeStep >= 2 ? (
                      <img src={stepCheckmarkIcon} alt="Completed" className="w-4 h-3" />
                    ) : (
                      <span className="font-['Rubik'] font-medium text-xl text-brand-blue">1</span>
                    )}
                  </div>

                  {/* Connecting line - blue when step 2 active */}
                  <div className={`w-[180px] h-[6px] rounded-full ${activeStep >= 2 ? 'bg-brand-blue' : 'bg-neutral-100'}`} />

                  {/* Step 2 circle */}
                  <div
                    className={`w-[34px] h-[34px] rounded-full flex items-center justify-center ${
                      activeStep >= 2 ? 'border-solid border-2 border-brand-blue bg-transparent' : 'bg-neutral-100'
                    }`}
                  >
                    <span className={`font-['Rubik'] text-xl ${activeStep >= 2 ? 'text-brand-blue font-medium' : 'text-white font-normal'}`}>2</span>
                  </div>
                </div>

                {/* Step Labels — same width as circles + line + gaps above */}
                <div className="flex items-start" style={{ width: `${34 + 8 + 180 + 8 + 34}px` }}>
                  <div className="flex flex-col items-center" style={{ width: '34px', flexShrink: 0 }}>
                    <span className="font-['Rubik'] font-medium text-xs text-neutral-800 whitespace-nowrap">Step 1</span>
                    <span className="font-['Rubik'] font-normal text-xs text-form-border whitespace-nowrap">Define experiment</span>
                  </div>
                  <div className="flex-1" />
                  <div className="flex flex-col items-center" style={{ width: '34px', flexShrink: 0 }}>
                    <span className="font-['Rubik'] font-medium text-xs text-neutral-800 whitespace-nowrap">Step 2</span>
                    <span className="font-['Rubik'] font-normal text-xs text-form-border whitespace-nowrap">Processing options</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Body - conditional on active step, scrollable when content overflows */}
            <div className="flex-1 overflow-y-auto w-full min-h-0">
              {activeStep === 1 ? (
                <CreateExperimentStep1
                  experimentId={form.values.id}
                  title={form.values.title}
                  onTitleChange={(v) => form.setFieldValue('title', v)}
                  description={form.values.description}
                  onDescriptionChange={(v) => form.setFieldValue('description', v)}
                  organism={form.values.organism}
                  onOrganismChange={(v) => form.setFieldValue('organism', v)}
                  titleError={form.errors.title as string | undefined}
                  descriptionError={form.errors.description as string | undefined}
                  candidateInformation={form.values.candidateInformation}
                  onCandidateInformationChange={(fileLocation) => form.setFieldValue('candidateInformation', fileLocation)}
                  onFileDrop={handleFileDrop}
                />
              ) : (
                <CreateExperimentStep2
                  useDefaultClonotyping={form.values.useDefaultClonotyping}
                  onUseDefaultClonotypingChange={(v) => form.setFieldValue('useDefaultClonotyping', v)}
                  includeUnpairedHeavyChains={form.values.includeUnpairedHeavyChains}
                  onIncludeUnpairedHeavyChainsChange={(v) => form.setFieldValue('includeUnpairedHeavyChains', v)}
                  clonotypeDefinition={form.values.clonotypeDefinition}
                  onClonotypeDefinitionChange={(v) => form.setFieldValue('clonotypeDefinition', v)}
                  threshold={form.values.threshold}
                  onThresholdChange={(v) => form.setFieldValue('threshold', v)}
                />
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ justifyContent: activeStep === 1 ? 'flex-end' : 'space-between' }}>
              {/* Back button - only on Step 2, left side */}
              {activeStep === 2 && (
                <Button
                  variant="outline"
                  size="compact-lg"
                  onClick={handleBack}
                  radius="xl"
                  px="md"
                  leftSection={<img src={backButtonIcon} alt="" className="w-3 h-3" />}
                  color="#0064B0"
                  className="font-['Rubik'] font-medium text-xs"
                >
                  Back
                </Button>
              )}

              {/* Right side buttons */}
              <div className="flex items-center gap-2">
                {/* Cancel */}
                <Button
                  variant="subtle"
                  color="#0064B0"
                  size="compact-lg"
                  onClick={handleClose}
                  radius="xl"
                  px="md"
                  className="font-['Rubik'] font-medium text-xs"
                >
                  Cancel
                </Button>

                {/* Next / Create */}
                <Button
                  variant="filled"
                  size="compact-lg"
                  color={activeStep === 1 && hasStep1Errors ? '#969EAE' : '#0064B0'}
                  onClick={activeStep === 1 && hasStep1Errors ? undefined : activeStep === 1 ? handleNext : handleCreate}
                  loading={isCreatingExperiment}
                  radius="xl"
                  px="md"
                  className={`font-['Rubik'] font-medium text-xs text-white ${activeStep === 1 && hasStep1Errors ? 'cursor-not-allowed' : ''}`}
                >
                  {activeStep === 1 ? 'Next' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default CreateExperimentsModal;
